import { uuid } from "@/interfaces";
import { ChatService } from "@/services/chat.service";
import { store } from "@/lib/store";
import { cancellationReasonFrom } from "@/lib/appointment-cancellation";
import type { ChatApprovalRequest } from "@shyne/shared/api";
import { api } from "@/lib/api";

export enum ApprovalRequestType {
  RESCHEDULE = "reschedule_request",
  CANCEL = "cancel_request",
  SERVICE_CHANGE = "service_change_request",
  REASSIGNMENT = "reassignment_request",
}

export interface ApprovalRequestData {
  type: ApprovalRequestType;
  appointmentId: uuid;
  requestedBy: uuid;
  requestedByName: string;
  requestedByRole: "pmc" | "vendor" | "individual";
  data: any; // Specific data for each request type
}

export interface RescheduleRequestData {
  currentDate: string;
  newDate: string;
}

export interface CancelRequestData {
  reason?: string;
}

export interface ServiceChangeRequestData {
  currentServiceId: uuid;
  currentServiceName: string;
  newServiceId: uuid;
  newServiceName: string;
  parentCategory: string;
}

export type ApprovalRequestToSend = ChatApprovalRequest;

export const AppointmentApprovalService = {
  async requestServiceChangeAsAssignee(appointmentId: uuid, service: uuid): Promise<void> {
    const actingOrganization = store.getState().organization.activeOrgId;
    await api.appointments.requestServiceChange(appointmentId, { service, ...(actingOrganization ? { actingOrganization } : {}) });
  },

  async sendApprovalRequest(roomId: uuid, request: ApprovalRequestToSend): Promise<uuid> {
    const actingOrganization = store.getState().organization.activeOrgId;
    if (!actingOrganization) throw new Error("Choose an organization to ask on behalf of.");

    return (await ChatService.post(roomId, { kind: "approvalRequest", actingOrganization, request })).id;
  },

  async approveRequest(roomId: uuid, approvalMessageId: uuid, requestData: ApprovalRequestData): Promise<void> {
    switch (requestData.type) {
      case ApprovalRequestType.RESCHEDULE: {
        const data = requestData.data as RescheduleRequestData;
        await this.executeReschedule(requestData.appointmentId, data.newDate);
        break;
      }

      case ApprovalRequestType.CANCEL: {
        const data = requestData.data as CancelRequestData;
        await this.executeCancel(requestData.appointmentId, data?.reason);
        break;
      }

      case ApprovalRequestType.SERVICE_CHANGE: {
        const data = requestData.data as ServiceChangeRequestData;
        await this.executeServiceChange(requestData.appointmentId, data.newServiceId);
        break;
      }

      case ApprovalRequestType.REASSIGNMENT: {
        await this.promptForAnAssigneeIfNeeded(requestData.appointmentId, roomId);
        break;
      }
    }

    await ChatService.post(roomId, { kind: "approvalAnswer", request: approvalMessageId, approved: true });
  },

  async rejectRequest(roomId: uuid, approvalMessageId: uuid, reason?: string): Promise<void> {
    await ChatService.post(roomId, { kind: "approvalAnswer", request: approvalMessageId, approved: false, ...(reason ? { reason } : {}) });
  },

  async executeReschedule(appointmentId: uuid, newDate: string): Promise<void> {
    const { rescheduleAppointment } = await import("@/services/appointment-reschedule.service");

    await rescheduleAppointment(appointmentId, newDate);
  },

  async executeCancel(appointmentId: uuid, requestedReason?: string): Promise<void> {
    const { cancelAppointment } = await import("@/services/appointment-cancellation.service");

    await cancelAppointment(appointmentId, cancellationReasonFrom(requestedReason));
  },

  async executeServiceChange(appointmentId: uuid, newServiceId: uuid): Promise<void> {
    const { changeService } = await import("@/services/appointment-change.service");

    await changeService(appointmentId, newServiceId);
  },

  async promptForAnAssigneeIfNeeded(appointmentId: uuid, roomId: uuid): Promise<void> {
    const { stillNeedsAnAssignee } = await import("@/services/appointment-assignment.service");

    if (await stillNeedsAnAssignee(appointmentId)) {
      await this.triggerAssignmentModal(appointmentId, roomId);
    }
  },

  async triggerAssignmentModal(appointmentId: uuid, roomId: uuid): Promise<void> {
    await ChatService.post(roomId, { kind: "assignmentPrompt" });
  },
};
